<script setup>
import { computed, ref } from 'vue';
import Draggable from 'vuedraggable';
import SurveyQuestionCard from './SurveyQuestionCard.vue';
import { useSurveyLabels } from '../composables/useSurveyLabels';

/**
 * Question list of the builder: add, remove, retype and reorder.
 *
 * Reordering works with a pointer (drag handle) and with the keyboard (the
 * up/down buttons inside each card), because a survey is often authored on a
 * laptop and reviewed on a tablet.
 */
const props = defineProps({
  questions: { type: Array, default: () => [] },
  questionErrors: { type: Object, default: () => ({}) },
  listError: { type: String, default: '' },
});

const emit = defineEmits([
  'reorder',
  'add',
  'update',
  'changeType',
  'remove',
  'duplicate',
  'moveUp',
  'moveDown',
  'addOption',
  'removeOption',
  'updateOption',
]);

const { t, questionTypeOptions } = useSurveyLabels();

const showTypePicker = ref(false);

const total = computed(() => props.questions.length);
const isEmpty = computed(() => total.value === 0);

const errorsFor = key => props.questionErrors[key] || {};

const addQuestion = type => {
  showTypePicker.value = false;
  emit('add', type);
};
</script>

<template>
  <section class="flex flex-col gap-4">
    <header class="flex flex-wrap items-end justify-between gap-3">
      <div class="min-w-0">
        <h2
          class="font-redHatDisplay text-lais-heading-sm font-semibold text-lais-text"
        >
          {{ t('SURVEYS.EDITOR.QUESTIONS.TITLE') }}
        </h2>
        <p class="mt-1 text-lais-body-sm text-lais-text-body">
          {{ t('SURVEYS.EDITOR.QUESTIONS.SUBTITLE') }}
        </p>
      </div>
      <span
        class="rounded-full bg-lais-bg-soft px-2.5 py-1 text-lais-label-sm font-semibold tabular-nums text-lais-text-body"
      >
        {{ t('SURVEYS.EDITOR.QUESTIONS.COUNT', { count: total }) }}
      </span>
    </header>

    <p
      v-if="listError"
      class="flex items-start gap-2 rounded-lais border border-lais-danger/40 bg-lais-danger-50 px-3 py-2 text-[13px] text-lais-danger"
      role="alert"
    >
      <span
        class="i-lucide-triangle-alert mt-0.5 size-4 shrink-0"
        aria-hidden="true"
      />
      {{ listError }}
    </p>

    <div
      v-if="isEmpty"
      class="grid place-items-center gap-2 rounded-lais border border-dashed border-lais-border bg-lais-bg-card px-4 py-10 text-center"
    >
      <span
        class="i-lucide-list-plus size-8 text-lais-text-muted"
        aria-hidden="true"
      />
      <p class="text-lais-body-md font-semibold text-lais-text">
        {{ t('SURVEYS.EDITOR.QUESTIONS.EMPTY_TITLE') }}
      </p>
      <p class="max-w-sm text-lais-body-sm text-lais-text-helper">
        {{ t('SURVEYS.EDITOR.QUESTIONS.EMPTY_BODY') }}
      </p>
    </div>

    <Draggable
      v-else
      :model-value="questions"
      item-key="key"
      handle=".survey-drag-handle"
      ghost-class="survey-question-ghost"
      animation="180"
      tag="div"
      class="flex flex-col gap-3"
      @update:model-value="list => emit('reorder', list)"
    >
      <template #item="{ element, index }">
        <SurveyQuestionCard
          :question="element"
          :index="index"
          :total="total"
          :questions="questions"
          :errors="errorsFor(element.key)"
          @update="patch => emit('update', index, patch)"
          @change-type="type => emit('changeType', index, type)"
          @remove="emit('remove', index)"
          @duplicate="emit('duplicate', index)"
          @move-up="emit('moveUp', index)"
          @move-down="emit('moveDown', index)"
          @add-option="emit('addOption', index)"
          @remove-option="
            optionIndex => emit('removeOption', index, optionIndex)
          "
          @update-option="
            (optionIndex, value) =>
              emit('updateOption', index, optionIndex, value)
          "
        />
      </template>
    </Draggable>

    <div class="flex flex-col gap-2">
      <button
        type="button"
        class="inline-flex h-11 items-center justify-center gap-2 rounded-lais border border-dashed border-lais-border bg-white text-[14px] font-semibold text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50/40 focus-visible:outline-none focus-visible:shadow-lais-focus"
        :aria-expanded="showTypePicker"
        @click="showTypePicker = !showTypePicker"
      >
        <span class="i-lucide-plus size-4" aria-hidden="true" />
        {{ t('SURVEYS.EDITOR.QUESTIONS.ADD') }}
      </button>

      <div
        v-if="showTypePicker"
        class="grid gap-2 rounded-lais border border-lais-border-soft bg-lais-bg-card p-3 sm:grid-cols-2 xl:grid-cols-3"
      >
        <button
          v-for="option in questionTypeOptions"
          :key="option.value"
          type="button"
          class="flex min-w-0 items-start gap-2.5 rounded-lais border border-lais-border-soft bg-white p-3 text-left transition hover:border-lais-primary hover:shadow-lais-card focus-visible:outline-none focus-visible:shadow-lais-focus"
          @click="addQuestion(option.value)"
        >
          <span
            class="mt-0.5 size-4 shrink-0 text-lais-primary"
            :class="option.icon"
            aria-hidden="true"
          />
          <span class="min-w-0">
            <span class="block text-[13px] font-semibold text-lais-text">
              {{ option.label }}
            </span>
            <span class="block text-[12px] leading-snug text-lais-text-muted">
              {{ option.helper }}
            </span>
          </span>
        </button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.survey-question-ghost {
  opacity: 0.4;
}
</style>
